import { Fragment, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  CogIcon,
  UserIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

import { Button, Input } from "components/ui";
import { toast } from "sonner";
import { useTemplateParameters } from "services/templateParameterService";

let lastActiveTabIndex = 0;

export function ManageVariablesModal({ isOpen, onClose, onInsert }) {
  const {
    templateVariables,
    templateCategories,
    isLoading: isLoadingVariables,
    error: variablesError,
    hasInitialized,
    fetchParameters,
    setTemplateVariables
  } = useTemplateParameters();

  const [selectedTabIndex, setSelectedTabIndex] = useState(lastActiveTabIndex);

  const handleTabChange = (index) => {
    setSelectedTabIndex(index);
    lastActiveTabIndex = index;
  };

  useEffect(() => {
    if (isOpen && !hasInitialized) {
      fetchParameters();
    }
  }, [isOpen, hasInitialized, fetchParameters]);

  const handleVariableUpdate = (key, value) => {
    setTemplateVariables(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const insertVariable = (key) => {
    if (onInsert) {
      onInsert(key);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[1000] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={onClose}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/40 dark:bg-dark-900/60 transition-opacity backdrop-blur-sm" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white dark:bg-dark-800 text-left align-middle shadow-2xl transition-all">
            <div className="flex flex-col h-full max-h-[85vh]">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 dark:border-dark-600 px-4 py-4 sm:px-5 bg-gray-50/50 dark:bg-dark-800/50">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
                    <span className="text-xl">{"{}"}</span>
                  </div>
                  <DialogTitle
                    as="h3"
                    className="text-base font-medium text-gray-800 dark:text-dark-100"
                  >
                    Template Variables
                  </DialogTitle>
                </div>
                <Button
                  onClick={onClose}
                  variant="flat"
                  isIcon
                  className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
                >
                  <XMarkIcon className="size-5" />
                </Button>
              </div>

              {/* Body */}
              <div className="flex flex-col overflow-y-auto px-4 py-4 sm:px-5">
                <div className="space-y-3 max-h-[32rem] overflow-y-auto">
                  {isLoadingVariables ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-dark-100 mb-2">
                        Loading Variables...
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-dark-400">
                        Fetching template variables from server
                      </p>
                    </div>
                  ) : variablesError ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                        <span className="text-2xl">⚠️</span>
                      </div>
                      <h3 className="text-sm font-medium text-red-900 dark:text-red-100 mb-2">
                        Failed to Load Variables
                      </h3>
                      <p className="text-xs text-red-600 dark:text-red-400 mb-4">
                        {variablesError}
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outlined"
                        color="primary"
                        onClick={fetchParameters}
                        className="text-xs px-4 py-2"
                      >
                        Retry
                      </Button>
                    </div>
                  ) : Object.keys(templateVariables).length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center">
                        <span className="text-2xl">📝</span>
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-dark-100 mb-2">
                        No Variables Available
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-dark-400">
                        No template variables are currently configured in your system.
                      </p>
                    </div>
                  ) : templateCategories && templateCategories.length > 0 ? (
                    <TabGroup selectedIndex={selectedTabIndex} onChange={handleTabChange}>
                      <TabList className="border-b border-gray-200 dark:border-dark-600 -mb-px flex space-x-8">
                        {templateCategories.map((category) => (
                          <Tab
                            key={category.CategoryId}
                            className={({ selected }) =>
                              `whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors focus:outline-none inline-flex items-center gap-2 ${selected
                                ? "border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-500"
                                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-dark-400 dark:hover:border-dark-500 dark:hover:text-dark-300"
                              }`
                            }
                          >
                            {category.CategoryName === 'System' ? (
                              <CogIcon className="w-4 h-4" />
                            ) : category.CategoryName === 'User' ? (
                              <UserIcon className="w-4 h-4" />
                            ) : category.CategoryName === 'Company' ? (
                              <BuildingOfficeIcon className="w-4 h-4" />
                            ) : category.CategoryName === 'Lead' ? (
                              <ChartBarIcon className="w-4 h-4" />
                            ) : (
                              <DocumentTextIcon className="w-4 h-4" />
                            )}
                            <span>{category.CategoryName}</span>
                          </Tab>
                        ))}
                      </TabList>

                      <TabPanels className="mt-1">
                        {templateCategories.map((category) => (
                          <TabPanel key={category.CategoryId} className="space-y-3">
                            {category.Description && (
                              <p className="text-xs text-gray-500 dark:text-dark-400 mb-1">
                                {category.Description}
                              </p>
                            )}

                            {category.Parameters && category.Parameters.length > 0 ? (
                              category.Parameters.map((param) => {
                                const key = param.ParameterKey?.replace(/[{}]/g, '');
                                if (!key) return null;
                                const value = templateVariables[key] || param.DefaultValue || '';

                                return (
                                  <div
                                    key={param.ParameterId}
                                    className="group relative bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-lg p-4 hover:shadow-md transition-all duration-200"
                                  >
                                    <div className="space-y-4">
                                      <div className="flex items-center justify-between gap-4">
                                        <div className="flex items-center gap-3">
                                          <div className="inline-flex items-center gap-2 bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-dark-200 px-3 py-1.5 rounded-lg text-sm font-medium">
                                            <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                                            <code className="font-mono">{key}</code>
                                          </div>
                                          <code className="text-xs bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 px-2 py-1 rounded font-mono text-primary-600 dark:text-primary-400">
                                            {param.ParameterKey}
                                          </code>
                                        </div>

                                        <div className="flex items-center gap-2">
                                          <Button
                                            type="button"
                                            size="sm"
                                            variant="outlined"
                                            onClick={() => {
                                              navigator.clipboard.writeText(`{{${key}}}`);
                                              toast.success(`Copied {{${key}}} to clipboard!`);
                                            }}
                                            className="justify-center text-xs font-medium py-2 px-3 rounded-md border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-dark-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-all duration-150 flex items-center gap-1"
                                          >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                              />
                                            </svg>
                                            Copy
                                          </Button>
                                          <Button
                                            type="button"
                                            size="sm"
                                            variant="solid"
                                            color="primary"
                                            onClick={() => insertVariable(key)}
                                            className="justify-center text-xs font-semibold py-2 px-3 rounded-md bg-primary-600 hover:bg-primary-700 text-white shadow-sm hover:shadow transition-all duration-150"
                                          >
                                            Insert
                                          </Button>
                                        </div>
                                      </div>

                                      <div className="space-y-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-dark-300">
                                          Preview Value
                                        </label>
                                        <Input
                                          value={value}
                                          onChange={(e) => handleVariableUpdate(key, e.target.value)}
                                          placeholder="Enter preview value"
                                          className="bg-white dark:bg-dark-800"
                                        />
                                        <p className="text-xs text-gray-500 dark:text-dark-400">
                                          This value will be shown in the template preview
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <p className="text-xs text-gray-500 dark:text-dark-400">
                                No variables available in this category.
                              </p>
                            )}
                          </TabPanel>
                        ))}
                      </TabPanels>
                    </TabGroup>
                  ) : (
                    // Fallback: flat list if categories not available
                    Object.entries(templateVariables).map(([key, value]) => (
                      <div
                        key={key}
                        className="group relative bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-lg p-4 hover:shadow-md transition-all duration-200"
                      >
                        <div className="space-y-4">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                              <div className="inline-flex items-center gap-2 bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-dark-200 px-3 py-1.5 rounded-lg text-sm font-medium">
                                <span className="w-2 h-2 bg-primary-500 rounded-full"></span>
                                <code className="font-mono">{key}</code>
                              </div>
                              <code className="text-xs bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 px-2 py-1 rounded font-mono text-primary-600 dark:text-primary-400">
                                {'{{' + key + '}}'}
                              </code>
                            </div>

                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                size="sm"
                                variant="outlined"
                                onClick={() => {
                                  navigator.clipboard.writeText(`{{${key}}}`);
                                  toast.success(`Copied {{${key}}} to clipboard!`);
                                }}
                                className="justify-center text-xs font-medium py-2 px-3 rounded-md border border-gray-300 dark:border-dark-600 text-gray-700 dark:text-dark-300 hover:bg-gray-50 dark:hover:bg-dark-700 transition-all duration-150 flex items-center gap-1"
                              >
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                  />
                                </svg>
                                Copy
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="solid"
                                color="primary"
                                onClick={() => insertVariable(key)}
                                className="justify-center text-xs font-semibold py-2 px-3 rounded-md bg-primary-600 hover:bg-primary-700 text-white shadow-sm hover:shadow transition-all duration-150"
                              >
                                Insert
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-dark-300">
                              Preview Value
                            </label>
                            <Input
                              value={value}
                              onChange={(e) => handleVariableUpdate(key, e.target.value)}
                              placeholder="Enter preview value"
                              className="bg-white dark:bg-dark-800"
                            />
                            <p className="text-xs text-gray-500 dark:text-dark-400">
                              This value will be shown in the template preview
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Actions */}
                <div className="mt-6 flex justify-between items-center">
                  <div className="text-xs text-gray-500 dark:text-dark-400">
                    {Object.keys(templateVariables).length} variables available
                  </div>
                  <div className="space-x-3">
                    <Button
                      type="button"
                      onClick={onClose}
                      variant="outlined"
                      className="min-w-[7rem] rounded-full"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}

ManageVariablesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onInsert: PropTypes.func,
};
